import {
	createContext,
	type MutableRefObject,
	type ReactElement,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
} from "react";

/**
 * Result of committing pending Details / form autosave edits.
 */
export type DetailsCommitResult<TField extends string = string> =
	| { ok: true }
	| {
			ok: false;
			field: TField;
			fieldLabel: string;
			retry: () => Promise<DetailsCommitResult<TField>>;
	  };

/**
 * Registerable controller for pending autosave + nav guards.
 */
export interface DetailsSaveController<TField extends string = string> {
	hasPending: () => boolean;
	commitPending: () => Promise<DetailsCommitResult<TField>>;
}

/**
 * Registry surface exposed to panels and navigation guards.
 */
export interface DetailsSaveRegistryValue<TField extends string = string> {
	controllerRef: MutableRefObject<DetailsSaveController<TField> | null>;
	registerController: (
		controller: DetailsSaveController<TField> | null,
	) => void;
	getController: () => DetailsSaveController<TField> | null;
}

export interface DetailsSaveRegistryApi<TField extends string = string> {
	Provider: (props: { children: ReactNode }) => ReactElement;
	useRegistry: () => DetailsSaveRegistryValue<TField> | null;
	useController: () => DetailsSaveController<TField> | null;
}

/**
 * Creates an isolated Details/form save registry (provider + hooks).
 * Each entity (job, ticket, asset, record) should call this once at module scope.
 */
export function createDetailsSaveRegistry<
	TField extends string = string,
>(): DetailsSaveRegistryApi<TField> {
	const Context = createContext<DetailsSaveRegistryValue<TField> | null>(null);

	function Provider({ children }: { children: ReactNode }): ReactElement {
		const controllerRef = useRef<DetailsSaveController<TField> | null>(null);

		const registerController = useCallback(
			(controller: DetailsSaveController<TField> | null) => {
				controllerRef.current = controller;
			},
			[],
		);

		const getController = useCallback(() => controllerRef.current, []);

		const value = useMemo(
			() => ({ controllerRef, registerController, getController }),
			[getController, registerController],
		);

		return <Context.Provider value={value}>{children}</Context.Provider>;
	}

	function useRegistry(): DetailsSaveRegistryValue<TField> | null {
		return useContext(Context);
	}

	function useController(): DetailsSaveController<TField> | null {
		const registry = useContext(Context);
		return registry?.controllerRef.current ?? null;
	}

	return { Provider, useRegistry, useController };
}
